import { useContext, useEffect, useState } from 'react'
import { getRequest, postRequest } from '../../../../services/base'
import { useNavigate, useParams } from 'react-router-dom'
import { NavContext } from '../../../../context/global_context'
import { notifyError } from '../../../../utils/notifications'
import InputText from '../../../common/InputText'
import SelectInput from '../../../common/SelectInput'
import { Card, Flex, Form, Row, Space, Typography } from 'antd'
import TextArea from '../../../common/TextArea'
import InlineCheckbox from '../../../common/InlineCheckbox'
import Button from '../../../common/Button'
import { useIntl } from 'react-intl'

const CreateDtcForm = () => {
  const navigate = useNavigate()
  const [value, dispatch] = useContext(NavContext)
  const [form] = Form.useForm()
  const intl = useIntl()

  let { vehicle } = useParams()
  const [payload, setPayload] = useState({
    create_dtc_circuits: false,
    clone_with_existing_mapping: false,
    target_dtcs: [],
    active: false,
    destination_only: false,
    short_description: '',
    description: '',
    description_2: '',
    ecu: '',
    name: '',
    make: vehicle,
  })
  const [ecusData, setEcusData] = useState([])

  useEffect(() => {
    getEcuData('')

    dispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({ id: 'layout.adminTools' }),
        sub_header: `Vehicle List / ${vehicle} / DTCs / Tracer Authoring Mappings / Create New DTC`,
        id: vehicle,
        show_back_btn: true,
        dtc_details: [],
        back_btn_url: `/vehicles/${vehicle}/dtcs/listing`,
      },
    })
  }, [])

  const saveDtcs = async () => {
    dispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'dtcs.create.saving' }),
        showLoader: true,
      },
      notification: {},
    })
    let res = await postRequest(`/api/dtcs/save/new/`, payload)
    if (res?.status) {
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'dtcs.create.saveSuccess' }, { name: res.data.name }),
        },
      })
      setTimeout(() => {
        navigate(`/vehicles/${vehicle}/dtcs/listing/${res.data.id}/edit`)
      }, 500)
    } else {
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'dtcs.create.saveError' }),
        },
      })
    }
  }

  const getEcuData = async (search) => {
    let response = await getRequest(
      `/api/ecu/ecu/?vehicle=${vehicle}&search=${search}`
    )
    // The length check below leaves a failure completely silent, so the failure
    // branch has to be explicit: base.js returns {status: false}, not a rejection.
    if (response?.status === false) {
      notifyError(dispatch, intl.formatMessage({ id: 'dtcs.ecusLoadError' }))
      return
    }
    if (response?.data?.length > 0) {
      setEcusData(response.data)
    }
  }

  const onEcuInputSearch = (option, searchText) => {
    if (option.label.toLowerCase().includes(searchText.toLowerCase())) {
      return true
    } else {
      return false
    }
  }

  return (
    <>
      <div className="page dtc-create">
        <Row className="title">
          <Flex vertical gap={16} style={{ width: '100%' }}>
            <Typography.Title level={5}>
              {intl.formatMessage({ id: 'dtcs.create.title' })}
            </Typography.Title>
          </Flex>
        </Row>
        <Row className="body">
          <Form
            id="dtc-create-form"
            name="dtc-create-form"
            form={form}
            layout="vertical"
            onFinish={() => saveDtcs()}
          >
            <Card title="" variant="outlined">
              <Flex gap={16} vertical>
                <Flex gap={16} justify='space-evenly' align='center'>
                  <InputText
                    type="text"
                    name="name"
                    label={intl.formatMessage({ id: 'dtcs.create.name' })}
                    isRequired={true}
                    onChange={(value) => {
                      setPayload({ ...payload, name: value })
                    }}
                    wrapperClass="justify-items-evenly"
                  />

                  <SelectInput
                    name='select_ecu'
                    label={intl.formatMessage({ id: 'dtcs.create.selectEcu' })}
                    options={ecusData}
                    value={ecusData.filter((option) => option.label === payload?.ecu)}
                    placeholder={intl.formatMessage({ id: 'dtcs.create.selectEcu' })}
                    allowClear={true}
                    wrapperClass='select-ecu'
                    onChange={(option) => {
                      setPayload({ ...payload, ecu: option?.value })
                    }}
                    filterOption={(input, option) => {
                      return onEcuInputSearch(option, input)
                    }}
                  />
                </Flex>
                <Flex gap={16}>
                  <TextArea
                    name="short_description"
                    label={intl.formatMessage({ id: 'dtcs.create.shortDescription' })}
                    value={payload.short_description}
                    isRequired={false}
                    maxLength={128}
                    wrapperClass="dtc-edit-text-area"
                    onChange={(value) => {
                      setPayload({ ...payload, short_description: value })
                    }}
                  />

                  <TextArea
                    name="description"
                    value={payload.description}
                    label={intl.formatMessage({ id: 'dtcs.create.description' })}
                    isRequired={false}
                    maxLength={128}
                    wrapperClass="dtc-edit-text-area"
                    onChange={(value) => {
                      setPayload({ ...payload, description: value })
                    }}
                  />

                  <TextArea
                    name="description_2"
                    value={payload.description_2}
                    label={intl.formatMessage({ id: 'dtcs.create.description2' })}
                    isRequired={false}
                    wrapperClass="dtc-edit-text-area"
                    onChange={(value) => {
                      setPayload({ ...payload, description_2: value })
                    }}
                  />
                </Flex>

                <InlineCheckbox
                  name="destination_only"
                  label={intl.formatMessage({ id: 'dtcs.create.destinationOnly' })}
                  value={payload?.destination_only}
                  wrapperClass="justify-items-evenly"
                  onChange={(value) => {
                    setPayload({ ...payload, destination_only: value })
                  }}
                />

                <InlineCheckbox
                  name="active"
                  label={intl.formatMessage({ id: 'dtcs.create.active' })}
                  value={payload?.active}
                  wrapperClass="justify-items-evenly"
                  onChange={(value) => {
                    setPayload({ ...payload, active: value })
                  }}
                />
              </Flex>
            </Card>
            <Space size="middle">
              <Button
                htmlType="submit"
                type="primary"
                className="submit-button"
                testId="dtc-save"
              >
                {intl.formatMessage({ id: 'dtcs.create.saveDtc' })}
              </Button>
              <Button
                className="cancel-button"
                onClick={() => navigate(`/vehicles/${vehicle}/dtcs/listing`)}
              >
                {intl.formatMessage({ id: 'forms.cancel' })}
              </Button>
            </Space>
          </Form>
        </Row>
      </div>
    </>
  )
}

export default CreateDtcForm
